import React, { useState, useEffect } from 'react';
import { getAllTopicsExercisesStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const LessonMenu = ({ onSelectLesson, onOpenDashboard, onOpenAdmin }) => {
  const { user, logout } = useAuth();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const lessons = [
    {
      id: 1,
      topic: 'family_hobbies',
      title: 'Představování se, koníčky, rodina',
      description: 'Umím se anglicky představit, říct něco o své rodině a koníčcích',
      image: '/images/family.jpg'
    },
    {
      id: 2,
      topic: 'shopping_cafe',
      title: 'Nakupování, v kavárně, restauraci',
      description: 'Umím si objednat v kavárně nebo restauraci a zaplatit',
      image: '/images/cafe.jpg'
    },
    {
      id: 3,
      topic: 'travel',
      title: 'Cesty, cestování, cizina a cizinci',
      description: 'Umím mluvit o cestování, dovolené a dopravních prostředcích',
      image: '/images/travel.jpg'
    },
    {
      id: 4,
      topic: 'weather_health',
      title: 'Počasí, nemoci, klima, budoucnost',
      description: 'Umím mluvit o počasí, o tom, jak se cítím, a dávat a přijímat jednoduché rady',
      image: '/images/weather.jpg'
    },
    {
      id: 5,
      topic: 'work_internet',
      title: 'Práce, Internet, mediální gramotnost',
      description: 'Umím mluvit o používání internetu, o svých online aktivitách a o práci',
      image: '/images/work.jpg'
    }
  ];

  // Fetch progress data on mount
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        const data = await getAllTopicsExercisesStatus();
        setProgressData(data.topics);
      } catch (error) {
        console.error('Error fetching progress:', error);
        // Continue without progress data
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  // Helper function to render lesson completion status
  const renderLessonStatus = (topic) => {
    if (!progressData || !progressData[topic]) {
      return (
        <div style={{
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#999',
          fontWeight: '500',
          fontSize: '0.9rem'
        }}>
          ○ Nedokončeno
        </div>
      );
    }

    const topicProgress = progressData[topic];
    const writingExercise = topicProgress.exercises.find(ex => ex.exercise_type === 'writing');
    const isLessonCompleted = writingExercise?.finished === true;

    return (
      <div style={{
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: isLessonCompleted ? '#28a745' : '#999',
        fontWeight: '500',
        fontSize: '0.9rem'
      }}>
        {isLessonCompleted ? '✓ Dokončeno' : '○ Nedokončeno'}
      </div>
    );
  };

  const isLessonLocked = (index) => {
    if (index === 0) return false; // First lesson always unlocked

    // If we don't have progress data yet, assume locked to be safe
    if (!progressData) return true;

    const previousLesson = lessons[index - 1];
    const previousTopicProgress = progressData[previousLesson.topic];

    // If no data for previous lesson, it's not started/finished -> locked
    if (!previousTopicProgress) return true;

    // Check if writing exercise in previous lesson is finished
    const writingExercise = previousTopicProgress.exercises.find(ex => ex.exercise_type === 'writing');
    return !writingExercise?.finished;
  };

  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        {/* Navigační tlačítka */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={onOpenDashboard}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '14px 24px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Můj pokrok</span>
            </button>
            {user && user.is_admin && (
              <button
                onClick={onOpenAdmin}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '14px 24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = '#ccc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Admin</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#666' }}>
              {user?.username}
            </span>
            <button
              onClick={logout}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '14px 20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Odhlásit</span>
            </button>
          </div>
        </div>

        <h1 style={{
          fontSize: '2rem',
          fontWeight: '500',
          marginBottom: '8px',
          color: '#1a1a1a'
        }}>
          English Teacher
        </h1>

        <p style={{
          fontSize: '1rem',
          color: '#666',
          marginBottom: '40px'
        }}>
          Vyberte lekci
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {lessons.map((lesson, index) => {
            const locked = isLessonLocked(index);

            return (
              <button
                key={lesson.id}
                onClick={() => !locked && onSelectLesson(lesson.topic)}
                disabled={locked}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '0',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: locked ? 0.7 : 1,
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!locked) {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.borderColor = '#ccc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!locked) {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                {locked && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    🔒
                  </div>
                )}
                <div style={{
                  width: '100%',
                  height: '160px',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  filter: locked ? 'grayscale(100%)' : 'none'
                }}>
                  <img
                    src={lesson.image}
                    alt={lesson.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="color: #999; font-size: 0.9rem;">Image</div>';
                    }}
                  />
                </div>
                <div style={{
                  padding: '20px'
                }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: '#1a1a1a'
                  }}>
                    {lesson.title}
                  </h3>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4'
                  }}>
                    {lesson.description}
                  </p>

                  {/* Lesson completion status */}
                  {renderLessonStatus(lesson.topic)}

                  <div style={{
                    fontSize: '0.8rem',
                    color: lesson.color, // Note: lesson.color is undefined in the lessons array above, but keeping it as is from original code
                    fontWeight: '500',
                    marginTop: '10px'
                  }}>
                    Lekce {lesson.id}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LessonMenu;
